import { hashForAnalytics } from "@src/utils/analyticsSanitize"
import { trackEvent } from "@src/utils/trackEvent"
import type { PermanentRejectionReason } from "../types"

const EVENTS = {
  dashboardViewed: "referral_dashboard_viewed",
  linkLanded: "referral_link_landed",
  linkRejected: "referral_link_rejected",
  attributionStarted: "referral_attribution_started",
  attributionCompleted: "referral_attribution_completed",
  attributed: "referral_attributed",
  codeGenerated: "referral_code_generated",
  linkCopied: "referral_link_copied",
  linkShared: "referral_link_shared",
  referredByBannerViewed: "referral_referred_by_banner_viewed",
  welcomeDialogShown: "referral_welcome_dialog_shown",
  welcomeDialogConfirmed: "referral_welcome_dialog_confirmed",
  welcomeDialogDismissed: "referral_welcome_dialog_dismissed",
} as const

type ReferralCodePayload = {
  referral_code_hash: string
  referral_code_length: number
}

async function referralCodePayload(code: string): Promise<ReferralCodePayload> {
  return {
    referral_code_hash: await hashForAnalytics(code.toLowerCase()),
    referral_code_length: code.length,
  }
}

function trackReferralEvent(
  name: (typeof EVENTS)[keyof typeof EVENTS],
  code: string | null,
  payload: Record<string, unknown> = {}
): void {
  void (async () => {
    trackEvent(name, {
      ...(code ? await referralCodePayload(code) : {}),
      ...payload,
    })
  })()
}

export type ReferralDashboardViewPayload = {
  state: "unauthenticated" | "no_code" | "has_code"
  stats_status?: "loading" | "loaded" | "inconsistent" | "error"
  total_referees?: number
  active_referees?: number
  inactive_referees?: number
  total_trades?: number
  has_active_referees?: boolean
}

export function trackReferralDashboardViewed(
  code: string | null,
  payload: ReferralDashboardViewPayload
): void {
  trackReferralEvent(EVENTS.dashboardViewed, code, payload)
}

export function trackReferralLinkLanded(code: string): void {
  trackReferralEvent(EVENTS.linkLanded, code)
}

export function trackReferralLinkRejected(reason: "invalid_code"): void {
  trackReferralEvent(EVENTS.linkRejected, null, { reason })
}

export function trackReferralAttributionStarted(code: string): void {
  trackReferralEvent(EVENTS.attributionStarted, code)
}

export function trackReferralAttributionCompleted(
  code: string,
  payload:
    | { outcome: "success"; attempts: number }
    | {
        outcome: "permanent_rejection"
        reason: PermanentRejectionReason
        attempts: number
      }
    | { outcome: "exhausted"; attempts: number }
): void {
  trackReferralEvent(EVENTS.attributionCompleted, code, payload)
}

export function trackReferralAttributed(code: string): void {
  trackReferralEvent(EVENTS.attributed, code)
}

export function trackReferralCodeGenerated(code: string): void {
  trackReferralEvent(EVENTS.codeGenerated, code)
}

export function trackReferralLinkCopied(code: string): void {
  trackReferralEvent(EVENTS.linkCopied, code, { method: "copy_button" })
}

export function trackReferralLinkShared(
  code: string,
  payload: {
    method: "native_share" | "clipboard_fallback"
    outcome: "success" | "failure" | "cancelled"
  }
): void {
  trackReferralEvent(EVENTS.linkShared, code, payload)
}

export function trackReferralReferredByBannerViewed(code: string): void {
  trackReferralEvent(EVENTS.referredByBannerViewed, code)
}

export function trackReferralWelcomeDialogShown(
  outcome: "success" | "not_claimable"
): void {
  trackReferralEvent(EVENTS.welcomeDialogShown, null, { outcome })
}

export function trackReferralWelcomeDialogConfirmed(
  outcome: "success" | "not_claimable"
): void {
  trackReferralEvent(EVENTS.welcomeDialogConfirmed, null, { outcome })
}

export function trackReferralWelcomeDialogDismissed(
  outcome: "success" | "not_claimable"
): void {
  trackReferralEvent(EVENTS.welcomeDialogDismissed, null, { outcome })
}
